import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { Card, Container, Row, Col } from 'wix-style-react';
import { DataHooks } from '@src/constants/data-hooks';
import { KBLinks } from '@src/constants';
import { Theme, AppSettings, TranslationsMap, TranslationKey } from '@src/types';
import { Actions } from '@src/context/actions/actions';
import { ThemeEditor } from './edit-controls/theme-editor';
import { ConsentButtonEditor } from './edit-controls/consent-button-editor';
import { PrivacyInfoEditor } from './edit-controls/privacy-info-editor';
import { PrivacyLinkEditor } from './edit-controls/privacy-link-editor';
import { SettingsButtonEditor } from './edit-controls/settings-button-editor';
import { DeclineAllContainer } from './edit-controls/decline-all/decline-all-container';
import { ThemePreview } from '@src/components/theme-preview';

type MappedActions = Pick<Actions, 'updatePendingAppSettings' | 'updatePendingTranslations'>;
type MappedProps = MappedActions & {
  translations: TranslationsMap;
  theme: Theme;
  disabled: boolean;
  kbLinks: KBLinks;
  declineAllEnabled: boolean;
};

export interface Props extends InjectedTranslateProps, MappedProps {}

class TextAndDisplayComponent extends React.PureComponent<Props> {
  onUpdateSetting = (updatedSetting: Partial<AppSettings>) => this.props.updatePendingAppSettings(updatedSetting);
  onUpdateTranslation = (updatedValue: Partial<TranslationsMap>) => this.props.updatePendingTranslations(updatedValue);

  onChangeTheme = (theme: Theme) => this.onUpdateSetting({ theme });
  onConsentButtonTextChange = (value: string) => this.onUpdateTranslation({ [TranslationKey.ConsentButton]: value });
  onSettingsButtonChange = (value: string) => this.onUpdateTranslation({ [TranslationKey.SettingButton]: value });
  onPrivacyLinkChange = (value: string) => this.onUpdateTranslation({ [TranslationKey.PrivacyLink]: value });
  onDeclineAllToggleChange = (enabled: boolean) => this.onUpdateSetting({ declineAllConfig: { enabled, geo: [] } });
  onDeclineAllTextChange = (value: string) => this.onUpdateTranslation({ [TranslationKey.DeclineAllButton]: value });
  onPrivacyInfoChange = (value: string) => this.onUpdateTranslation({ [TranslationKey.PrivacyInfo]: value });

  onRestorePrivacyInfo = () =>
    this.onUpdateTranslation({ [TranslationKey.PrivacyInfo]: this.props.t('policy.banner.text') });

  render() {
    const { t, theme, disabled, translations, kbLinks, declineAllEnabled } = this.props;

    return (
      <Container>
        <Row>
          <Col>
            <Card dataHook={DataHooks.TextAndDisplayComponent}>
              <Card.Header
                dataHook={DataHooks.TextAndDisplayHeader}
                title={t('text-and-display.title')}
                subtitle={t('text-and-display.subtitle')}
              />
              <Card.Divider />
              <Card.Content>
                <Container fluid>
                  <Row>
                    <Col span={4}>
                      <Container fluid>
                        <Row>
                          <Col>
                            <ThemeEditor theme={theme} onChangeTheme={this.onChangeTheme} />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <ConsentButtonEditor
                              value={translations[TranslationKey.ConsentButton]}
                              onChange={this.onConsentButtonTextChange}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <SettingsButtonEditor
                              value={translations[TranslationKey.SettingButton]}
                              onChange={this.onSettingsButtonChange}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <PrivacyLinkEditor
                              value={translations[TranslationKey.PrivacyLink]}
                              onChange={this.onPrivacyLinkChange}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <DeclineAllContainer
                              enabled={declineAllEnabled}
                              inputValue={translations[TranslationKey.DeclineAllButton]}
                              onToggleChange={this.onDeclineAllToggleChange}
                              onTextChange={this.onDeclineAllTextChange}
                              learnMoreKBLink={kbLinks.AddDeclineAllButton}
                            />
                          </Col>
                        </Row>
                      </Container>
                    </Col>
                    <Col span={8}>
                      <ThemePreview skin="banner" />
                    </Col>
                  </Row>
                </Container>
              </Card.Content>
              <Card.Divider />
              <Card.Content>
                <PrivacyInfoEditor
                  value={translations[TranslationKey.PrivacyInfo]}
                  onChange={this.onPrivacyInfoChange}
                  disabled={disabled}
                  learnMoreKBLink={kbLinks.DisplayCookieBannerOnSite}
                />
              </Card.Content>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export const TextAndDisplayCard = translate()(TextAndDisplayComponent);
